<script lang="ts">
	import { createForm } from 'felte';
	import { validator } from '@felte/validator-zod';
	import { z } from 'zod';

	import { getHubStores } from '$lib/stores/hubStores';
	import ButtonLink from '$lib/components/ButtonLink/ButtonLink.svelte';
	import { _ } from 'svelte-i18n';
	import HubLogo from '$lib/Signin/HubLogo.svelte';
	import { email } from './emailStore';
	import Input from '$lib/components/Inputs/Input.svelte';
	import Button from '$lib/components/Button/Button.svelte';
	import { toastMessages } from '$lib/stores/toastMessageStore';

	const { hub, restApi, api } = getHubStores();

	export let isResetPasswordFormDisplayed: boolean;
	export let isSparqAdminInfoModalDisplayed: boolean;

	let isPasswordReset = false;

	const schema = z.object({
		email: z.string().email(),
	});

	const { errors, form } = createForm<z.infer<typeof schema>>({
		extend: validator({ schema }),
		onSubmit: async (values) => {
			$email = values.email;

			try {
				const resp = await api.lookupHubUser({ hubId: $hub.id, email: values.email });

				if (resp.lookupHubUser.redirect_url) {
					window.location.assign(resp.lookupHubUser.redirect_url);
					return;
				}

				if (resp.lookupHubUser.action === 'third-party-idp') {
					isSparqAdminInfoModalDisplayed = true;
					return;
				}

				const resetPasswordResp = await restApi.requestResetPassword(values.email, $hub.id);
				if (!resetPasswordResp.error) isPasswordReset = true;
			} catch (error) {
				toastMessages.danger($_('session.server.general.error'), error);
			}
		},
	});
</script>

<div class="resetPasswordForm">
	<HubLogo />

	{#if !isPasswordReset}
		<div class="title">{$_('magic.link.header')}</div>
		<div class="heading">
			{@html $_('session.reset.password.hub.description.svelte', {
				values: { hubName: $hub.name },
			})}
		</div>

		<form use:form>
			<Input name="email" type="email" label={$_('common.email')} invalid={$errors.email?.[0]} />
			<div class="buttonWrapper">
				<Button type="submit">{$_('common.continue')}</Button>
			</div>
		</form>
		<div class="buttonLink">
			<ButtonLink on:click={() => (isResetPasswordFormDisplayed = false)}
				>{$_('session.reset.password.close')}</ButtonLink>
		</div>
	{:else}
		<div class="title">{$_('session.reset.password.sent.title')}</div>
		<div class="heading">
			{@html $_('session.reset.password.sent.body.svelte', { values: { email: $email } })}
		</div>
		<div class="buttonLink">
			<ButtonLink
				on:click={() => {
					isPasswordReset = false;
					isResetPasswordFormDisplayed = false;
				}}>{$_('session.reset.password.close.sent')}</ButtonLink>
		</div>
	{/if}
</div>

<style>
	.resetPasswordForm {
		display: flex;
		box-sizing: content-box;
		flex-direction: column;
		align-items: center;
		max-block-size: calc(100vh - 60px);
		max-inline-size: min-content;
		padding: 50px;
	}

	.buttonWrapper {
		margin-block: var(--gutterSmall);
	}

	.buttonWrapper :global(button) {
		inline-size: 100%;
	}

	.buttonLink {
		font-size: var(--fontSizeSmaller);
	}

	.title {
		font-size: var(--fontSizeLarge);
	}

	.heading {
		margin-block: var(--gutterLarge);
		margin-inline: 0;
		color: var(--BLACK);
		font-size: var(--fontSizeNormal);
		font-weight: 500;
		text-align: center;
	}
</style>
